import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    X, Send, User, ChevronRight, Loader2, CheckCircle,
    AlertTriangle, Info, ShieldCheck, Users, Globe, Building, Search, Target, Zap
} from 'lucide-react';
import { trackEvent, AdoptionEvents } from '../lib/analytics';
import { SnackbarContext } from '../App';
import api from '../api/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const EMPTY_ARRAY = [];

const SequenceEnrollModal = ({
    personIds = EMPTY_ARRAY,
    workspaceContactIds = EMPTY_ARRAY,
    groupId,
    listId,
    companyId,
    groupName,
    listName,
    companyName,
    preSelectedSequenceId,
    onClose,
    onComplete
}) => {
    const showSnackbar = useContext(SnackbarContext);
    const [step, setStep] = useState('SELECT'); // SELECT -> SELECT_AUDIENCE -> PREFLIGHT -> RESULTS
    const [sequences, setSequences] = useState([]);
    const [selectedSequenceId, setSelectedSequenceId] = useState(preSelectedSequenceId || '');
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [enrolling, setEnrolling] = useState(false);
    const [preflight, setPreflight] = useState(null);
    const [results, setResults] = useState(null);

    const hasAudienceSource = Boolean((personIds && personIds.length > 0) || (workspaceContactIds && workspaceContactIds.length > 0) || groupId || listId || companyId);

    // Dynamic Audience State
    const [audienceType, setAudienceType] = useState('CONTACTS');
    const [contactSearch, setContactSearch] = useState('');
    const [contacts, setContacts] = useState([]);
    const [selectedContactIds, setSelectedContactIds] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [lists, setLists] = useState([]);
    const [selectedListId, setSelectedListId] = useState('');

    const [hasSendingAccounts, setHasSendingAccounts] = useState(true);

    const handleRunPreflight = useCallback(async (overrideId) => {
        const seqId = overrideId || selectedSequenceId;
        if (!seqId) return;

        const finalContactIds = hasAudienceSource ? (workspaceContactIds.length > 0 ? workspaceContactIds : personIds) : (audienceType === 'CONTACTS' ? selectedContactIds : []);
        const finalGroupId = hasAudienceSource ? groupId : (audienceType === 'GROUPS' ? selectedGroupId : null);
        const finalListId = hasAudienceSource ? listId : (audienceType === 'LISTS' ? selectedListId : null);
        const finalCompanyId = hasAudienceSource ? companyId : null;

        setVerifying(true);
        try {
            const res = await api.post(`/api/sequences/${seqId}/pre-flight`, {
                contactIds: finalContactIds,
                groupId: finalGroupId,
                listId: finalListId,
                companyId: finalCompanyId
            });
            setPreflight(res.data);
            setStep('PREFLIGHT');
        } catch (err) {
            showSnackbar('Pre-flight check failed: ' + (err.response?.data?.message || err.message), 'error');
        } finally {
            setVerifying(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSequenceId, hasAudienceSource, audienceType, selectedContactIds, selectedGroupId, selectedListId, groupId, listId, companyId, personIds, workspaceContactIds, showSnackbar]);

    const handleEnroll = async () => {
        setEnrolling(true);
        try {
            const finalContactIds = hasAudienceSource ? (workspaceContactIds.length > 0 ? workspaceContactIds : personIds) : (audienceType === 'CONTACTS' ? selectedContactIds : []);
            const finalGroupId = hasAudienceSource ? groupId : (audienceType === 'GROUPS' ? selectedGroupId : null);
            const finalListId = hasAudienceSource ? listId : (audienceType === 'LISTS' ? selectedListId : null);
            const finalCompanyId = hasAudienceSource ? companyId : null;

            const enrollmentSource = finalGroupId ? 'GROUP_SELECTION' : 
                                    finalListId ? 'LIST_SELECTION' : 
                                    finalCompanyId ? 'COMPANY_CONTACT_SELECTION' :
                                    'MANUAL_CONTACT_SELECTION';

            const payload = { 
                contactIds: finalContactIds, 
                groupId: finalGroupId, 
                listId: finalListId,
                companyId: finalCompanyId,
                enrollmentSource
            };

            const res = await api.post(`/api/sequences/${selectedSequenceId}/enroll`, payload);
            setResults(res.data);
            setStep('RESULTS');

            trackEvent(AdoptionEvents.SEQUENCE_LAUNCH, {
                sequenceId: selectedSequenceId,
                enrolledCount: res.data.enrolled,
                failedCount: res.data.failed,
                source: enrollmentSource
            });
        } catch (err) {
            showSnackbar('Enrollment failed: ' + (err.response?.data?.message || err.message), 'error');
        } finally {
            setEnrolling(false);
        }
    };

    useEffect(() => {
        const fetchSequencesAndAccounts = async () => {
            try {
                const [seqRes, accRes, poolRes] = await Promise.all([
                    api.get('/api/sequences'),
                    api.get('/api/mail-accounts'),
                    api.get('/api/sender-pools')
                ]);
                setSequences(seqRes.data.filter(s => s.status === 'ACTIVE'));
                const accounts = accRes.data.accounts || accRes.data.data || (Array.isArray(accRes.data) ? accRes.data : []);
                const pools = poolRes.data.pools || poolRes.data.data || (Array.isArray(poolRes.data) ? poolRes.data : []);
                setHasSendingAccounts(accounts.some(a => a.active !== false) || pools.some(p => p.isActive));
            } catch (err) {
                console.error('Failed to fetch data', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSequencesAndAccounts();
    }, []);

    useEffect(() => {
        if (preSelectedSequenceId) {
            if (hasAudienceSource) {
                setStep('PREFLIGHT');
                handleRunPreflight(preSelectedSequenceId);
            } else {
                setStep('SELECT_AUDIENCE');
            }
        }
    }, [preSelectedSequenceId, hasAudienceSource, handleRunPreflight]);

    useEffect(() => {
        if (step === 'SELECT_AUDIENCE') {
            api.get('/api/groups').then(res => setGroups(res.data?.data || res.data || [])).catch(console.error);
            api.get('/api/lists').then(res => setLists(res.data?.data || res.data || [])).catch(console.error);
        }
    }, [step]);

    useEffect(() => {
        if (step === 'SELECT_AUDIENCE' && audienceType === 'CONTACTS') {
            const fetchContacts = async () => {
                try {
                    const res = await api.get(`/api/contacts?limit=50&search=${contactSearch}`);
                    setContacts(res.data.data || []);
                } catch (e) { console.error(e); }
            };
            const to = setTimeout(fetchContacts, 300);
            return () => clearTimeout(to);
        }
    }, [step, audienceType, contactSearch]);

    const renderSummaryCard = (label, count, icon, colorClass, bgColor) => (
        <Card className={`overflow-hidden border-2 ${colorClass} bg-white shadow-sm transition-all hover:shadow-md`}>
            <CardContent className="p-5 flex flex-col items-center justify-center text-center gap-2">
                <div className={`p-3 rounded-2xl ${bgColor} flex items-center justify-center`}>
                    {React.cloneElement(icon, { className: `w-5 h-5 ${colorClass.split(' ')[0]}` })}
                </div>
                <div className="space-y-0.5">
                    <span className="text-2xl font-black block tracking-tight">{count}</span>
                    <span className="text-[10px] uppercase font-black opacity-50 tracking-[0.1em] block">{label}</span>
                </div>
            </CardContent>
        </Card>
    );

    if (step === 'RESULTS' && results) {
        const totalProcessed = (results.enrolled || 0) + (results.skipped || 0) + (results.failed || 0);
        return (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
                <div className="bg-card border-2 border-border/40 w-full max-w-xl rounded-[3rem] p-12 shadow-[0_30px_80px_rgba(0,0,0,0.1)] animate-in zoom-in-95 duration-500 flex flex-col items-center text-center">
                    
                    <div className="w-20 h-20 bg-emerald-500/10 rounded-3xl flex items-center justify-center mb-8 shadow-inner overflow-hidden relative group">
                        <CheckCircle className="w-10 h-10 text-emerald-600 relative z-10" />
                        <div className="absolute inset-0 bg-emerald-500/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                    </div>

                    <h3 className="text-3xl font-black tracking-tighter text-foreground mb-3">Orchestration Complete</h3>
                    <p className="text-muted-foreground text-sm font-medium mb-10 max-w-xs leading-relaxed">
                        Successfully processed <span className="text-foreground font-black">{totalProcessed}</span> prospects for deployment.
                    </p>
                    
                    <div className="w-full grid grid-cols-3 gap-5 mb-10">
                        {renderSummaryCard('Enrolled', results.enrolled, <Send />, 'text-emerald-600 border-emerald-500/20', 'bg-emerald-500/10')}
                        {renderSummaryCard('Skipped', results.skipped, <Info />, 'text-blue-600 border-blue-500/20', 'bg-blue-500/10')}
                        {renderSummaryCard('Failed', results.failed, <AlertTriangle />, 'text-rose-600 border-rose-500/20', 'bg-rose-500/10')}
                    </div>

                    {results.failureDetails?.length > 0 && (
                        <div className="w-full bg-muted/30 border-2 border-border/40 rounded-3xl p-6 mb-10 text-left">
                            <h4 className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                                <AlertTriangle className="w-3.5 h-3.5 text-rose-500" /> Intelligence Log
                            </h4>
                            <div className="space-y-3 max-h-40 overflow-y-auto pr-3 custom-scrollbar">
                                {results.failureDetails.map((d, i) => (
                                    <div key={i} className="flex gap-3 text-xs font-bold items-start group">
                                        <div className="w-1.5 h-1.5 rounded-full bg-rose-500/40 mt-1.5 shrink-0 group-hover:scale-125 transition-transform" />
                                        <span className="text-muted-foreground group-hover:text-foreground transition-colors">{d.reason || 'Anomalous validation error'}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <Button 
                        size="lg"
                        onClick={onComplete}
                        className="w-full h-16 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-primary/20"
                    >
                        Return to Workspace
                    </Button>
                </div>
            </div>
        );
    }

    if (step === 'PREFLIGHT' && preflight) {
        const { summary } = preflight;
        return (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
                <div className="bg-card border-2 border-border/40 w-full max-w-2xl rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.1)] overflow-hidden animate-in zoom-in-95 duration-500">
                    
                    <div className="p-8 border-b border-border/40 flex items-center justify-between bg-muted/10">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shadow-inner">
                                <Zap className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black tracking-tight text-foreground uppercase">Pre-flight Intelligence</h3>
                                <p className="text-muted-foreground text-[10px] uppercase font-black tracking-[0.2em] mt-0.5">
                                    Simulating outreach for <span className="text-foreground">{summary.total}</span> profile(s)
                                </p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2.5 hover:bg-destructive hover:text-white rounded-xl transition-all active:scale-95 group">
                            <X className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                        </button>
                    </div>

                    <div className="p-10">
                        <div className="grid grid-cols-4 gap-4 mb-10">
                            {renderSummaryCard('Deployable', summary.eligible, <ShieldCheck />, 'text-emerald-600 border-emerald-500/20', 'bg-emerald-500/10')}
                            {renderSummaryCard('Engaged', summary.alreadyActive, <Users />, 'text-blue-600 border-blue-500/20', 'bg-blue-500/10')}
                            {renderSummaryCard('Excluded', summary.blocked + summary.invalidEmail + summary.suppressed, <X />, 'text-rose-600 border-rose-500/20', 'bg-rose-500/10')}
                            {renderSummaryCard('Warning', summary.risky, <AlertTriangle />, 'text-amber-600 border-amber-500/20', 'bg-amber-500/10')}
                        </div>

                        <div className="space-y-4 mb-10">
                            {summary.warnings > 0 && (
                                <div className="bg-amber-500/5 border-2 border-amber-500/20 rounded-3xl p-6 flex items-start gap-5 group hover:bg-amber-500/10 transition-colors">
                                    <div className="w-10 h-10 rounded-2xl bg-amber-500/10 flex items-center justify-center shrink-0">
                                        <Building className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div className="space-y-1">
                                        <h4 className="text-amber-700 font-black text-xs uppercase tracking-tight">Account Saturation Warning</h4>
                                        <p className="text-amber-700/70 text-[11px] font-medium leading-relaxed">
                                            We identified {summary.warnings} prospects whose organizations are already being nurtured in concurrent or active sequences.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {!hasSendingAccounts && (
                                <div className="bg-rose-500/5 border-2 border-rose-500/20 rounded-3xl p-6 flex items-start gap-5 group hover:bg-rose-500/10 transition-colors">
                                    <div className="w-10 h-10 rounded-2xl bg-rose-500/10 flex items-center justify-center shrink-0">
                                        <AlertTriangle className="w-5 h-5 text-rose-600" />
                                    </div>
                                    <div className="space-y-1">
                                        <h4 className="text-rose-700 font-black text-xs uppercase tracking-tight">Critical Infrastructure Missing</h4>
                                        <p className="text-rose-700/70 text-[11px] font-medium leading-relaxed">
                                            No valid sending proxy detected. Enrollment is allowed but prospects will be cached as <span className="text-rose-700 font-bold underline decoration-dotted">PENDING</span> until a mailbox is connected.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col items-center py-6 bg-muted/5 border-2 border-dashed border-border/60 rounded-[2rem]">
                            <div className="text-[10px] uppercase font-black text-muted-foreground tracking-[0.3em] mb-2 opacity-50 italic">Calculated Launch Size</div>
                            <div className="text-6xl font-black text-foreground tracking-tighter drop-shadow-sm">{summary.eligible}</div>
                            <div className="mt-4 flex gap-2">
                                <Badge variant="outline" className="bg-white px-3 py-1 font-black text-[9px] uppercase border-border/40">Real-time Verified</Badge>
                                <Badge variant="outline" className="bg-white px-3 py-1 font-black text-[9px] uppercase border-border/40">Deduplicated</Badge>
                            </div>
                        </div>
                    </div>

                    <div className="p-8 bg-muted/10 border-t border-border/40 flex gap-5">
                        <Button 
                            variant="ghost"
                            onClick={onClose} 
                            className="flex-1 h-14 rounded-2xl font-black text-[10px] uppercase tracking-widest text-muted-foreground border-2 border-border/40 hover:bg-background"
                        >
                            Abort Launch
                        </Button>
                        <Button 
                            disabled={summary.eligible === 0 || enrolling}
                            onClick={handleEnroll}
                            className="flex-[1.5] h-14 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-primary/20 flex items-center justify-center gap-3"
                        >
                            {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            {enrolling ? `Initiating Sequence...` : `Initiate & Synchronize`}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (step === 'SELECT_AUDIENCE') {
        const canGoNext = (audienceType === 'CONTACTS' && selectedContactIds.length > 0) || 
                          (audienceType === 'GROUPS' && selectedGroupId) || 
                          (audienceType === 'LISTS' && selectedListId);
                          
        return (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
                <div className="bg-card border-2 border-border/40 w-full max-w-xl rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.1)] overflow-hidden animate-in zoom-in-95 duration-500">
                    
                    <div className="p-8 border-b border-border/40 flex items-center justify-between bg-muted/10">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shadow-inner">
                                <Users className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black tracking-tight text-foreground uppercase">Define Audience</h3>
                                <p className="text-muted-foreground text-[10px] uppercase font-black tracking-[0.2em] mt-0.5">
                                    Strategic Prospect Assignment
                                </p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2.5 hover:bg-destructive hover:text-white rounded-xl transition-all active:scale-95 group">
                            <X className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                        </button>
                    </div>

                    <div className="p-10">
                        <div className="flex bg-muted/40 rounded-2xl p-1.5 border-2 border-border/40 mb-8">
                            {[
                                { id: 'CONTACTS', label: 'Individuals', icon: <User className="w-3.5 h-3.5" /> },
                                { id: 'GROUPS', label: 'Clusters', icon: <Building className="w-3.5 h-3.5" /> },
                                { id: 'LISTS', label: 'Segments', icon: <Target className="w-3.5 h-3.5" /> }
                            ].map(t => (
                                <button 
                                    key={t.id}
                                    onClick={() => setAudienceType(t.id)} 
                                    className={`flex-1 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest py-3 rounded-xl transition-all ${audienceType === t.id ? 'bg-white text-primary shadow-lg border-2 border-primary/5' : 'text-muted-foreground hover:bg-white/50'}`}
                                >
                                    {t.icon} {t.label}
                                </button>
                            ))}
                        </div>

                        {audienceType === 'CONTACTS' && (
                            <div className="space-y-5">
                                <div className="relative group">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                    <input
                                        type="text"
                                        placeholder="Intelligence-based profile search..."
                                        value={contactSearch}
                                        onChange={(e) => setContactSearch(e.target.value)}
                                        className="w-full h-14 bg-background border-2 border-border/40 rounded-2xl pl-12 pr-6 text-xs font-bold focus:border-primary outline-none transition-all shadow-inner"
                                    />
                                </div>
                                <div className="max-h-[320px] overflow-y-auto border-2 border-border/40 rounded-[2rem] p-3 space-y-1.5 custom-scrollbar bg-muted/10">
                                    {contacts.map(c => (
                                        <label key={c.id} className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all border-2 ${selectedContactIds.includes(c.id) ? 'bg-white border-primary/20 shadow-sm' : 'hover:bg-white border-transparent'}`}>
                                            <input 
                                                type="checkbox" 
                                                checked={selectedContactIds.includes(c.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) setSelectedContactIds(prev => [...prev, c.id]);
                                                    else setSelectedContactIds(prev => prev.filter(id => id !== c.id));
                                                }}
                                                className="w-5 h-5 rounded-lg border-border/60 text-primary focus:ring-primary shadow-sm"
                                            />
                                            <div className="flex flex-col gap-0.5 overflow-hidden">
                                                <span className="text-xs font-black text-foreground truncate">{c.displayName || c.firstName + ' ' + c.lastName}</span>
                                                <span className="text-[10px] text-muted-foreground font-bold tracking-tight opacity-60 truncate underline decoration-primary/20">{c.canonicalEmail || c.email}</span>
                                            </div>
                                        </label>
                                    ))}
                                    {contacts.length === 0 && (
                                        <div className="py-12 text-center flex flex-col items-center opacity-30 grayscale gap-2">
                                            <Search className="w-8 h-8" />
                                            <p className="text-[10px] font-black uppercase tracking-[0.2em]">No profiles identified</p>
                                        </div>
                                    )}
                                </div>
                                <div className="flex justify-end">
                                    <Badge variant="secondary" className="px-4 py-1.5 rounded-full font-black text-[9px] uppercase tracking-widest bg-primary/5 text-primary border-primary/20">
                                        {selectedContactIds.length} Target(s) Selected
                                    </Badge>
                                </div>
                            </div>
                        )}

                        {audienceType === 'GROUPS' && (
                            <div className="space-y-4">
                                <div className="max-h-[320px] overflow-y-auto border-2 border-border/40 rounded-[2rem] p-3 space-y-2 custom-scrollbar bg-muted/10">
                                    {groups.map(g => (
                                        <button 
                                            key={g._id}
                                            onClick={() => setSelectedGroupId(g._id)}
                                            className={`w-full text-left flex items-center gap-5 p-5 rounded-2xl cursor-pointer transition-all border-2 ${selectedGroupId === g._id ? 'bg-white border-primary shadow-md' : 'hover:bg-white border-transparent'}`}
                                        >
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${selectedGroupId === g._id ? 'bg-primary text-white' : 'bg-background border-2 border-border/40 text-muted-foreground'}`}>
                                                <Building className="w-5 h-5" />
                                            </div>
                                            <div className="flex flex-col gap-0.5">
                                                <span className={`text-xs font-black uppercase tracking-tight ${selectedGroupId === g._id ? 'text-primary' : 'text-foreground'}`}>{g.name}</span>
                                                <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest opacity-60">{g.isDynamic ? 'Dynamic Population' : 'Static Membership'}</span>
                                            </div>
                                        </button>
                                    ))}
                                    {groups.length === 0 && (
                                        <div className="py-12 text-center opacity-30 grayscale font-black text-[10px] uppercase tracking-widest">No group architectures found.</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {audienceType === 'LISTS' && (
                            <div className="space-y-4">
                                <div className="max-h-[320px] overflow-y-auto border-2 border-border/40 rounded-[2rem] p-3 space-y-2 custom-scrollbar bg-muted/10">
                                    {lists.map(l => (
                                        <button 
                                            key={l._id}
                                            onClick={() => setSelectedListId(l._id)}
                                            className={`w-full text-left flex items-center gap-5 p-5 rounded-2xl cursor-pointer transition-all border-2 ${selectedListId === l._id ? 'bg-white border-primary shadow-md' : 'hover:bg-white border-transparent'}`}
                                        >
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${selectedListId === l._id ? 'bg-primary text-white' : 'bg-background border-2 border-border/40 text-muted-foreground'}`}>
                                                <Target className="w-5 h-5" />
                                            </div>
                                            <div className="flex flex-col gap-0.5">
                                                <span className={`text-xs font-black uppercase tracking-tight ${selectedListId === l._id ? 'text-primary' : 'text-foreground'}`}>{l.name}</span>
                                                <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest opacity-60">{l.memberCount || 0} Unified Members</span>
                                            </div>
                                        </button>
                                    ))}
                                    {lists.length === 0 && (
                                        <div className="py-12 text-center opacity-30 grayscale font-black text-[10px] uppercase tracking-widest">No segments available.</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="p-8 bg-muted/10 border-t border-border/40 flex gap-5">
                        <Button 
                            variant="ghost" 
                            onClick={onClose} 
                            className="flex-1 h-14 rounded-2xl font-black text-[10px] uppercase tracking-widest text-muted-foreground border-2 border-border/40 hover:bg-background"
                        >
                            Cancel
                        </Button>
                        <Button 
                            disabled={!canGoNext || verifying}
                            onClick={() => handleRunPreflight()}
                            className="flex-[1.5] h-14 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-primary/20 flex items-center justify-center gap-3"
                        >
                            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                            {verifying ? `Verifying...` : `Proceed to Pre-flight`}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-card border-2 border-border/40 w-full max-w-xl rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.1)] overflow-hidden animate-in zoom-in-95 duration-500">
                
                <div className="p-8 border-b border-border/40 flex items-center justify-between bg-muted/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shadow-inner">
                            <Globe className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-black tracking-tight text-foreground uppercase">Launch Orchestration</h3>
                            <p className="text-muted-foreground text-[10px] uppercase font-black tracking-[0.2em] mt-0.5">
                                SELECT DEPLOYMENT ARCHITECTURE
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2.5 hover:bg-destructive hover:text-white rounded-xl transition-all active:scale-95 group">
                        <X className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                    </button>
                </div>

                <div className="p-10">
                    {loading ? (
                        <div className="py-20 flex flex-col items-center gap-6 opacity-40">
                            <Loader2 className="w-10 h-10 text-primary animate-spin" />
                            <p className="text-[10px] font-black uppercase tracking-[0.3em]">Querying Active Protocols...</p>
                        </div>
                    ) : sequences.length === 0 ? (
                        <div className="py-16 text-center text-muted-foreground italic font-black text-sm uppercase tracking-widest opacity-30 grayscale">
                            No active sequence architectures identified.
                        </div>
                    ) : (
                        <div className="space-y-4 max-h-[420px] overflow-y-auto pr-3 custom-scrollbar">
                            {sequences.map(seq => (
                                <button 
                                    key={seq._id}
                                    onClick={() => setSelectedSequenceId(seq._id)}
                                    className={`w-full flex items-center justify-between p-6 rounded-[2rem] border-2 transition-all text-left group relative overflow-hidden ${
                                        selectedSequenceId === seq._id ? 'bg-white border-primary shadow-xl ring-4 ring-primary/5' : 'bg-muted/10 border-transparent hover:bg-white hover:border-border/60'
                                    }`}
                                >
                                    <div className="flex items-center gap-5 relative z-10">
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${selectedSequenceId === seq._id ? 'bg-primary text-white scale-110 shadow-lg' : 'bg-background border-2 border-border/40 text-muted-foreground group-hover:bg-primary/5 group-hover:text-primary'}`}>
                                            <Zap className="w-6 h-6" />
                                        </div>
                                        <div className="space-y-0.5">
                                            <div className={`font-black text-xs uppercase tracking-tight transition-colors ${selectedSequenceId === seq._id ? 'text-primary' : 'text-foreground'}`}>
                                                {seq.name}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="px-2 py-0 font-black text-[8px] uppercase tracking-widest border-border/40 bg-white">
                                                    {seq.isGraph ? 'Graph Logic' : 'Linear Flow'}
                                                </Badge>
                                                <span className="text-[9px] text-muted-foreground font-bold tracking-widest uppercase opacity-40 italic">Active Protocol</span>
                                            </div>
                                        </div>
                                    </div>
                                    {selectedSequenceId === seq._id && (
                                        <div className="absolute right-8 top-1/2 -translate-y-1/2">
                                            <div className="w-3 h-3 bg-primary rounded-full animate-ping opacity-20" />
                                            <div className="w-3 h-3 bg-primary rounded-full absolute inset-0" />
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-8 bg-muted/10 border-t border-border/40">
                    <Button 
                        disabled={!selectedSequenceId || verifying}
                        onClick={() => handleRunPreflight()}
                        className="w-full h-16 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-2xl shadow-primary/20 transition-all flex items-center justify-center gap-3"
                    >
                        {verifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <ChevronRight className="w-5 h-5" />}
                        {verifying ? `Verifying Payload...` : `Next: Pre-flight Intelligence`}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SequenceEnrollModal;
